import { InlineIconButton, RowButton } from "@/components/ui/pressable";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Inbox, Layers, Search, X } from "lucide-react";
import { useGetIdentity, useGetList, type RaRecord } from "ra-core";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useLocation, useNavigate } from "react-router";

import { Avatar } from "@/components/atomic-crm/contacts/Avatar";
import { CompanyAvatar } from "@/components/atomic-crm/companies/CompanyAvatar";
import { resolveEffectiveModules } from "@/components/atomic-crm/providers/commons/memberModuleAccess";
import type { AccessIdentity } from "@/components/atomic-crm/providers/commons/canAccess";
import type { Company, Contact } from "@/components/atomic-crm/types";
import { IconButton } from "@/components/ui/icon-button";
import { cn } from "@/lib/utils";
import {
  LBS_CONTACT_STATUSES_FOR_FILTER,
  LBS_LEAD_STATUSES_FOR_FILTER,
} from "@/app/navigation";
import { isLeadLifecycleStatus } from "@/modules/constants/contactStatus";
import { getClientShowPath, getPersonShowPath } from "@/app/routing";
import type { Ticket } from "@/modules/types";
import { ticketStatusLabel } from "@/modules/tickets/ticketInboxConfig";
import { formatTicketCardSubject } from "@/modules/tickets/ticketOverviewConfig";
import { ticketShowPath } from "@/modules/tickets/ticketStatusWorkflow";
import { sectionTitleForLocation } from "@/lib/documentTitle/documentTitle";
import { useWorkspaceTabsOptional } from "@/modules/workspace/workspaceTabsContext";

/**
 * Global Spotlight search. One button — searches across every module
 * the current user has access to (Accounts, Projects, Tickets) and renders
 * results grouped by module. The module matching the
 * current URL is shown first so search "starts where you are".
 */
export type SpotlightSearchButtonProps = {
  /** Override placeholder copy. */
  placeholder?: string;
  /** Soft cap per-module (defaults to 5). */
  perModuleLimit?: number;
  /**
   * "hidden" mounts the component without a visible trigger button —
   * useful for routes (like Messages) that suppress the global top bar
   * but still need the ⌘K listener to work.
   */
  variant?: "default" | "hidden";
  /**
   * Legacy backward-compat props kept so existing call sites don't break.
   * They are ignored by the global behavior but accepted to avoid churn.
   */
  title?: string;
  description?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  resource?: string;
  filter?: Record<string, unknown>;
  getHref?: (record: RaRecord) => string;
  renderItem?: (record: RaRecord, isActive: boolean) => ReactNode;
};

type ModuleId =
  | "accounts"
  | "leads"
  | "clients"
  | "contacts"
  | "deals"
  | "tickets"
  | "tabs";

type ResolvedSuggestion = {
  moduleId: ModuleId;
  moduleLabel: string;
  record: RaRecord;
  href: string;
  renderRow: () => ReactNode;
  /** Instead of navigating (an open tab is chosen, not opened again). */
  onSelect?: () => void;
};

const DEFAULT_LIMIT = 5;
const SEARCH_CONTACT_LIMIT = 15;

const leadFilter = () => ({
  "status@in": `(${LBS_LEAD_STATUSES_FOR_FILTER.map((s) => `"${s}"`).join(",")})`,
});

const contactFilter = () => ({
  "status@in": `(${LBS_CONTACT_STATUSES_FOR_FILTER.map((s) => `"${s}"`).join(",")})`,
});

const matchesPathByPrefix = (prefix: string) => (pathname: string) =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

const fullName = (record: {
  first_name?: string | null;
  last_name?: string | null;
}) => `${record.first_name ?? ""} ${record.last_name ?? ""}`.trim();

const truncatedJoin = (parts: Array<string | null | undefined>) =>
  parts.filter((p) => p && String(p).trim()).join(" · ") || "—";

const primaryEmail = (contact: Contact) =>
  contact.email_jsonb?.find((row) => row.email?.trim())?.email?.trim() ?? null;

export const SpotlightSearchButton = ({
  placeholder = "Search name, phone, email, or ticket…",
  perModuleLimit = DEFAULT_LIMIT,
  variant = "default",
}: SpotlightSearchButtonProps = {}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { identity } = useGetIdentity();

  const trimmedQuery = query.trim();
  const isSearching = trimmedQuery.length > 0;

  const modulePermissions = useMemo(
    () =>
      resolveEffectiveModules((identity as AccessIdentity | undefined) ?? null),
    [identity],
  );

  // Module access matrix — adapts to product mode (LBS vs contractor).
  const moduleAccess = useMemo(
    () => ({
      leads: modulePermissions.crm,
      clients: modulePermissions.crm,
      contacts: modulePermissions.crm,
      deals: modulePermissions.deal_operations,
      tickets: modulePermissions.support,
    }),
    [modulePermissions],
  );

  // Detect the "home" module from the current URL.
  const currentModule: ModuleId | null = useMemo(() => {
    if (
      matchesPathByPrefix("/accounts")(pathname) ||
      matchesPathByPrefix("/leads")(pathname) ||
      matchesPathByPrefix("/companies")(pathname) ||
      matchesPathByPrefix("/clients")(pathname) ||
      matchesPathByPrefix("/contacts")(pathname)
    ) {
      return "accounts";
    }
    if (matchesPathByPrefix("/deals")(pathname)) return "deals";
    if (matchesPathByPrefix("/tickets")(pathname)) return "tickets";
    return null;
  }, [pathname]);

  // Per-module queries (called in stable order — hooks rule).
  const unifiedContactsQuery = useGetList<Contact>(
    "contacts",
    {
      pagination: { page: 1, perPage: SEARCH_CONTACT_LIMIT },
      sort: { field: "last_seen", order: "DESC" },
      filter: { q: trimmedQuery },
    },
    {
      enabled:
        open && isSearching && (moduleAccess.leads || moduleAccess.contacts),
      staleTime: 15_000,
    },
  );

  const leadsQuery = useGetList(
    "contacts",
    {
      pagination: { page: 1, perPage: perModuleLimit },
      sort: { field: "last_seen", order: "DESC" },
      filter: trimmedQuery
        ? { ...leadFilter(), q: trimmedQuery }
        : leadFilter(),
    },
    {
      enabled: open && !isSearching && moduleAccess.leads,
      staleTime: 15_000,
    },
  );

  const clientsQuery = useGetList(
    "companies",
    {
      pagination: { page: 1, perPage: perModuleLimit },
      sort: { field: "name", order: "ASC" },
      filter: trimmedQuery ? { q: trimmedQuery } : {},
    },
    { enabled: open && moduleAccess.clients, staleTime: 15_000 },
  );

  const contactsQuery = useGetList(
    "contacts",
    {
      pagination: { page: 1, perPage: perModuleLimit },
      sort: { field: "last_name", order: "ASC" },
      filter: trimmedQuery
        ? { ...contactFilter(), q: trimmedQuery }
        : contactFilter(),
    },
    {
      enabled: open && !isSearching && moduleAccess.contacts,
      staleTime: 15_000,
    },
  );

  const dealsQuery = useGetList(
    "deals",
    {
      pagination: { page: 1, perPage: perModuleLimit },
      sort: { field: "updated_at", order: "DESC" },
      filter: trimmedQuery ? { q: trimmedQuery } : {},
    },
    { enabled: open && moduleAccess.deals, staleTime: 15_000 },
  );

  const ticketsQuery = useGetList<Ticket>(
    "tickets",
    {
      pagination: { page: 1, perPage: perModuleLimit },
      sort: { field: "updated_at", order: "DESC" },
      filter: trimmedQuery
        ? { q: trimmedQuery, "merged_into_ticket_id@is": null }
        : { "merged_into_ticket_id@is": null },
    },
    { enabled: open && moduleAccess.tickets, staleTime: 15_000 },
  );

  // Build grouped suggestion list. Each group is the module's section.
  const moduleGroups: {
    id: ModuleId;
    label: string;
    rows: ResolvedSuggestion[];
  }[] = useMemo(() => {
    const buildClientRow = (record: Company): ResolvedSuggestion => ({
      moduleId: "accounts",
      moduleLabel: "Accounts",
      record,
      href: getClientShowPath(record.id),
      renderRow: () => (
        <>
          <CompanyAvatar record={record} width={28} height={28} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">
              {record.name || "Untitled"}
            </p>
            <p className="truncate text-xs text-muted-foreground">
              {truncatedJoin([record.sector, record.website])}
            </p>
          </div>
        </>
      ),
    });

    const buildPersonRow = (record: Contact): ResolvedSuggestion => ({
      moduleId: "accounts",
      moduleLabel: "Accounts",
      record,
      href: getPersonShowPath(record),
      renderRow: () => (
        <>
          <Avatar record={record} width={28} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">
              {fullName(record) || record.company_name || "Untitled"}
            </p>
            <p className="truncate text-xs text-muted-foreground">
              {truncatedJoin([
                record.company_name,
                primaryEmail(record),
                isLeadLifecycleStatus(record.status)
                  ? record.lead_stage
                  : record.status,
              ])}
            </p>
          </div>
        </>
      ),
    });

    const buildDealRow = (
      record: RaRecord & {
        name?: string;
        company_name?: string | null;
        stage?: string;
        category?: string | null;
      },
    ): ResolvedSuggestion => ({
      moduleId: "deals",
      moduleLabel: "Projects",
      record,
      href: `/deals/${record.id}/show`,
      renderRow: () => (
        <>
          <div className="grid size-7 place-items-center rounded-md bg-muted text-2xs font-semibold uppercase text-muted-foreground">
            {(record.name ?? "?").slice(0, 2)}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">
              {record.name || "Untitled"}
            </p>
            <p className="truncate text-xs text-muted-foreground">
              {truncatedJoin([
                record.company_name,
                record.category,
                record.stage,
              ])}
            </p>
          </div>
        </>
      ),
    });

    const buildTicketRow = (record: Ticket): ResolvedSuggestion => ({
      moduleId: "tickets",
      moduleLabel: "Tickets",
      record,
      href: ticketShowPath(record.id, record.status),
      renderRow: () => (
        <>
          <div className="grid size-7 place-items-center rounded-md bg-muted text-muted-foreground">
            <Inbox className="size-3.5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">
              {formatTicketCardSubject(record.subject)}
            </p>
            <p className="truncate text-xs text-muted-foreground">
              {truncatedJoin([
                `#${record.id}`,
                ticketStatusLabel(record.status),
                record.requester_name,
                record.requester_email,
              ])}
            </p>
          </div>
        </>
      ),
    });

    const all: { id: ModuleId; label: string; rows: ResolvedSuggestion[] }[] =
      [];

    if (moduleAccess.leads || moduleAccess.contacts || moduleAccess.clients) {
      const accountRows: ResolvedSuggestion[] = [];
      if (moduleAccess.clients) {
        accountRows.push(...(clientsQuery.data ?? []).map(buildClientRow));
      }
      if (moduleAccess.leads || moduleAccess.contacts) {
        const peopleSource = isSearching
          ? (unifiedContactsQuery.data ?? [])
          : [
              ...(moduleAccess.leads ? (leadsQuery.data ?? []) : []),
              ...(moduleAccess.contacts ? (contactsQuery.data ?? []) : []),
            ];
        const seen = new Set<string>();
        for (const record of peopleSource) {
          const key = String(record.id);
          if (seen.has(key)) continue;
          seen.add(key);
          accountRows.push(buildPersonRow(record));
        }
      }
      // Keep companies first, then people; soft-cap total rows
      const companyRows = accountRows.filter((r) =>
        r.href.startsWith("/companies/"),
      );
      const peopleRows = accountRows.filter(
        (r) => !r.href.startsWith("/companies/"),
      );
      const merged = [
        ...companyRows.slice(0, perModuleLimit),
        ...peopleRows.slice(0, perModuleLimit),
      ];
      if (merged.length > 0) {
        all.push({ id: "accounts", label: "Accounts", rows: merged });
      }
    }
    if (moduleAccess.deals) {
      all.push({
        id: "deals",
        label: "Projects",
        rows: (dealsQuery.data ?? []).map(buildDealRow),
      });
    }
    if (moduleAccess.tickets) {
      const ticketRows = (ticketsQuery.data ?? []).map(buildTicketRow);
      if (ticketRows.length > 0) {
        all.push({
          id: "tickets",
          label: "Tickets",
          rows: ticketRows,
        });
      }
    }
    // Sort so the current module's section is rendered first.
    if (currentModule) {
      const idx = all.findIndex((g) => g.id === currentModule);
      if (idx > 0) {
        const [current] = all.splice(idx, 1);
        all.unshift(current);
      }
    }
    return all.filter((group) => group.rows.length > 0);
  }, [
    currentModule,
    isSearching,
    moduleAccess.clients,
    moduleAccess.contacts,
    moduleAccess.deals,
    moduleAccess.leads,
    moduleAccess.tickets,
    clientsQuery.data,
    contactsQuery.data,
    dealsQuery.data,
    leadsQuery.data,
    ticketsQuery.data,
    perModuleLimit,
    unifiedContactsQuery.data,
  ]);

  // Open workspace tabs first: choosing one switches to it.
  const workspace = useWorkspaceTabsOptional();
  const groupedSuggestions = useMemo(() => {
    if (!workspace?.enabled) return moduleGroups;
    const needle = trimmedQuery.toLowerCase();
    const rows: ResolvedSuggestion[] = workspace.tabs
      .filter((tab) => tab.id !== workspace.activeId)
      .filter((tab) => !needle || tab.title.toLowerCase().includes(needle))
      .map((tab) => ({
        moduleId: "tabs",
        moduleLabel: "Open tabs",
        record: { id: tab.id },
        href: `${tab.pathname}${tab.search}`,
        onSelect: () => workspace.selectTab(tab.id),
        renderRow: () => (
          <>
            <div className="grid size-7 place-items-center rounded-md bg-primary/10 text-primary">
              <Layers className="size-3.5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{tab.title}</p>
              <p className="truncate text-xs text-muted-foreground">
                Open tab
                {sectionTitleForLocation(tab.pathname, tab.search)
                  ? ` · ${sectionTitleForLocation(tab.pathname, tab.search)}`
                  : ""}
              </p>
            </div>
          </>
        ),
      }));
    return rows.length
      ? [{ id: "tabs" as const, label: "Open tabs", rows }, ...moduleGroups]
      : moduleGroups;
  }, [moduleGroups, trimmedQuery, workspace]);

  // Flat list used for keyboard navigation.
  const flatSuggestions = useMemo(
    () => groupedSuggestions.flatMap((g) => g.rows),
    [groupedSuggestions],
  );

  useEffect(() => {
    if (!open) {
      setQuery("");
      setActiveIndex(0);
    }
  }, [open]);

  useEffect(() => {
    setActiveIndex(0);
  }, [trimmedQuery]);

  // Global Cmd+K / Ctrl+K toggle. Skip when typing in an input/textarea
  // so it doesn't hijack normal form interactions.
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key !== "k" && event.key !== "K") return;
      if (!(event.metaKey || event.ctrlKey)) return;
      const target = event.target as HTMLElement | null;
      const inEditable =
        !!target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable);
      // Allow toggling even from an input — that's the whole point of
      // ⌘K. We just need to make sure we don't *also* type the letter.
      event.preventDefault();
      setOpen((prev) => !prev);
      void inEditable;
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const handleSelect = useCallback(
    (s: ResolvedSuggestion) => {
      setOpen(false);
      if (s.onSelect) s.onSelect();
      else navigate(s.href);
    },
    [navigate],
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (flatSuggestions.length === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((prev) => (prev + 1) % flatSuggestions.length);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex(
        (prev) => (prev - 1 + flatSuggestions.length) % flatSuggestions.length,
      );
    } else if (event.key === "Enter") {
      const target = flatSuggestions[activeIndex];
      if (target) {
        event.preventDefault();
        handleSelect(target);
      }
    }
  };

  const isFetching =
    (isSearching
      ? unifiedContactsQuery.isFetching
      : leadsQuery.isFetching || contactsQuery.isFetching) ||
    clientsQuery.isFetching ||
    dealsQuery.isFetching ||
    ticketsQuery.isFetching ||
    false;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      {variant === "hidden" ? null : (
        <DialogPrimitive.Trigger asChild>
          <IconButton
            variant="ghost"
            className="size-9 data-[state=open]:bg-accent"
            aria-label="Search CRM (⌘K)"
            title="Search CRM (⌘K)"
          >
            <Search className="size-4" />
          </IconButton>
        </DialogPrimitive.Trigger>
      )}
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            "fixed inset-0 z-50 bg-background/60 backdrop-blur-md",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          )}
        />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className={cn(
            "fixed left-[50%] top-[12%] z-50 w-full max-w-[calc(100%-2rem)]",
            "translate-x-[-50%] rounded-2xl bg-background shadow-overlay ring-1 ring-border/40",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "duration-150 sm:max-w-2xl",
          )}
        >
          <DialogPrimitive.Title className="sr-only">
            Search Sigma
          </DialogPrimitive.Title>

          <div className="relative flex items-center px-4 py-3">
            <Search className="pointer-events-none size-4 text-muted-foreground" />
            <input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              className="ml-3 flex-1 bg-transparent text-base outline-none placeholder:text-muted-foreground"
            />
            {query ? (
              <InlineIconButton
                size="sm"
                onClick={() => setQuery("")}
                aria-label="Clear search"
              >
                <X className="size-4" />
              </InlineIconButton>
            ) : null}
            <kbd className="ml-2 hidden rounded border bg-muted px-1.5 font-mono text-2xs text-muted-foreground sm:inline-block">
              ESC
            </kbd>
          </div>

          <div className="h-px bg-border/60" />

          <div className="max-h-[65vh] overflow-y-auto" role="listbox">
            {isFetching && flatSuggestions.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-muted-foreground">
                Searching…
              </p>
            ) : flatSuggestions.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-muted-foreground">
                {trimmedQuery
                  ? "No matches in the modules you can access."
                  : "Start typing to search Sigma."}
              </p>
            ) : (
              groupedSuggestions.map((group, groupIndex) => {
                const baseIndex = groupedSuggestions
                  .slice(0, groupIndex)
                  .reduce((acc, g) => acc + g.rows.length, 0);
                return (
                  <div key={group.id} className="py-1">
                    <div className="sticky top-0 z-10 bg-background px-4 py-1 text-2xs font-semibold uppercase tracking-wide text-muted-foreground">
                      {group.label}
                      {group.id === currentModule ? (
                        <span className="ml-2 rounded-sm bg-primary/10 px-1 py-0.5 text-2xs font-medium text-primary">
                          here
                        </span>
                      ) : null}
                    </div>
                    <div className="px-2 pb-1">
                      {group.rows.map((row, rowIndex) => {
                        const flatIndex = baseIndex + rowIndex;
                        const isActive = flatIndex === activeIndex;
                        return (
                          <RowButton
                            layout="none"
                            density="none"
                            className={cn(
                              "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors",
                              isActive
                                ? "bg-accent text-accent-foreground"
                                : "hover:bg-accent",
                            )}
                            key={`${row.moduleId}-${row.record.id}`}
                            role="option"
                            aria-selected={isActive}
                            onClick={() => handleSelect(row)}
                            onMouseEnter={() => setActiveIndex(flatIndex)}
                          >
                            {row.renderRow()}
                          </RowButton>
                        );
                      })}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
